import { any } from 'apps/web_forms/utils/arrays';
import reduxStore from 'apps/web_forms/reduxStore';

/**
 * Create an Array Tree for submitted data
 * @param {Object<String, String>} formSubmittedData
 */
export const buildSubmittedTree = (formSubmittedData) => {
  const metadataInputs = reduxStore.getState().form.metadataInputs;
  const formSubmittedTree = [];

  Object.entries(formSubmittedData).forEach(([inputIdentifier]) => {
    let inputValue = formSubmittedData[inputIdentifier];
    const splitInputIdentifier = inputIdentifier.split('/');

    let ref = formSubmittedTree;
    const endIndex = splitInputIdentifier.length - 1;
    for (let i = 0; i < endIndex; i++) {
      // eslint-disable-next-line prefer-const
      let [index, subIndex] = splitInputIdentifier[i].split('|');

      subIndex = subIndex || 0; // This is 0 in the subform case
      ref[index] = ref[index] || [];
      ref[index][subIndex] = ref[index][subIndex] || [];

      ref = ref[index][subIndex];
    }

    if (typeof inputValue !== 'object') {
      inputValue = { value: inputValue };
    }

    inputValue.metadata = { ...metadataInputs[inputIdentifier] };

    const { imageIdentifier } = inputValue;
    if (imageIdentifier) inputValue.metadata.imageIdentifier = imageIdentifier;

    ref[splitInputIdentifier[endIndex]] = inputValue;
  });

  return formSubmittedTree;
};

const buildXMLMetadata = (input) => {
  if (input.metadata) {
    return Object.entries(input.metadata).reduce(
      (string, [attribute, value]) =>
        value ? `${string} ${attribute}="${value}"` : string,
      ''
    );
  }
  return null;
};

/**
 * Clean special characters from XML input
 * @param {Object<String, String>} value
 */
const encodeXml = (value) => {
  if (typeof value !== 'string') return value;
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&#34;')
    .replace(/'/g, '&#39;');
};

/**
 * Create the XML final string based on the Array Tree
 * @param {Array} input
 */
export const parseXml = (inputs) => {
  let outputXml = '';
  Object.entries(inputs).forEach(([tag]) => {
    const input = inputs[tag];
    if (!Array.isArray(input)) {
      if (
        input.type === 'image' ||
        (input.constructor.name === 'FileList' && input.length === 0)
      ) {
        outputXml += `<${tag} ${buildXMLMetadata(input)} />`;
      } else if (input.value || input.value === 0) {
        outputXml += `<${tag} ${buildXMLMetadata(input)}>${encodeXml(
          input.value
        )}</${tag}>`;
      }
    } else {
      Object.keys(input).forEach((child) => {
        const content = input[child];
        outputXml += `<${tag}>${parseXml(content)}</${tag}>`;
      });
    }
  });
  return outputXml;
};

/**
 * Returns an object with all initialValues in the following format:
 *
 * {
 *  identifier: value,
 *  ...
 * }
 * @param {object} form
 */
export const getInitialAnswers = (form) => {
  return form.inputList.reduce(
    (prev, currentChild) => ({
      [currentChild.identifier]: currentChild.initialAnswer,
      ...prev,
    }),
    {}
  );
};

/**
 * @param {Object} field input from useXForm
 * @param {string} resourceIdentifier identifier hash of updated resource
 * @returns {boolean}
 */
const fieldDependsOnResource = (field, resourceIdentifier) => {
  let isDependentOnResource = false;

  if (field.type === 'calculated') {
    isDependentOnResource = field?.calculate_expr?.includes(resourceIdentifier);
  } else if (field.type === 'select') {
    isDependentOnResource = field?.options_resource === resourceIdentifier;
  } else if (field.type === 'sketch') {
    isDependentOnResource = field?.background_resource === resourceIdentifier;
  } else if (field.type === 'resource') {
    // "Attached File"
    isDependentOnResource = field?.resource_identifier === resourceIdentifier;
    // } else if (field.type === 'location') { // Not yet implemented, due to map overlays not yet being implemented in web forms
  } else {
    // otherwise, it shouldn't be, so leave as false (don't do anything)
  }

  if (field?.initialAnswerExpr) {
    isDependentOnResource =
      field.initialAnswerExpr.includes(resourceIdentifier);
  }

  return isDependentOnResource;
};

const MAX_DEPTH = Infinity;

/**
 * @param {[Object]} inputs inputList from useXForm
 * @returns {[Object]} flattened list of inputs
 */
const flattenInputs = (inputs, repeatGroupFields) => {
  return inputs
    .map((input) => {
      if (input.type !== 'group') {
        const repeatGroupFieldInstances = repeatGroupFields[input.identifier];
        if (
          repeatGroupFieldInstances &&
          Array.isArray(repeatGroupFieldInstances)
        ) {
          return repeatGroupFieldInstances.map((fieldInstanceIdentifier) => {
            return {
              ...input,
              identifier: fieldInstanceIdentifier,
            };
          });
        }
        return input;
      } else {
        return flattenInputs(input.children, repeatGroupFields);
      }
    })
    .flat(MAX_DEPTH);
};

/**
 * @param {[string]} updatedResources identifier hashes of updated resources
 * @param {[Object]} inputs inputList from useXForm
 */
export const getFieldsAffectedByUpdate = (
  updatedResources,
  inputs,
  repeatGroupFields
) => {
  if (!inputs || !Array.isArray(inputs) || !inputs.length) {
    return [];
  }

  return flattenInputs(inputs, repeatGroupFields).filter((input) => {
    return any(
      updatedResources.map((resourceIdentifier) =>
        fieldDependsOnResource(input, resourceIdentifier)
      )
    );
  });
};

export const flattenElementsArray = (arr) => {
  const mutatedElements = JSON.parse(JSON.stringify(arr));
  const flattened = [];

  function processObject(obj) {
    if (obj.children && obj.hasOwnProperty("maxOccurs")) {
      obj.children.forEach(child => {
        processObject(child);
      });
      delete obj.children;
    }
    flattened.push(obj);
  }

  mutatedElements.forEach(obj => {
    processObject(obj);
  });

  return flattened;
}

export const mergeFlattenedArrays = (array1, array2, identifierKey) => {
  const lookup = {};

  // Insert objects from array1 into the lookup table
  for (const obj of array1) {
    lookup[obj[identifierKey]] = obj;
  }

  // Merge objects from array2 into the lookup table
  for (const obj of array2) {
    lookup[obj[identifierKey]] = obj;
  }

  // Convert the lookup table values back to an array
  const mergedArray = Object.values(lookup);

  return mergedArray;
}

export const flattenElementsObject = (arr) => {
  const mutatedElements = JSON.parse(JSON.stringify(arr));
  const flattened = {};

  function processObject(obj) {
    if (obj.children && obj.hasOwnProperty("maxOccurs")) {
      obj.children.forEach(child => {
        processObject(child);
      });
      delete obj.children;
    }
    flattened[obj.identifier] = obj;
  }

  mutatedElements.forEach(obj => {
    processObject(obj);
  });

  return flattened;
}

export const mergeFlattenedObject = (object1, object2) => {
  const lookup = {...object1, ...object2}
  return lookup;
}

export const getParentIdentifier = (str) => {
  const parts = str.split('/');
  parts.pop();
  const lastPart = parts[parts.length - 1];
  if(lastPart && lastPart.includes("|")){
      parts[parts.length - 1] = lastPart.split('|')[0]
  }

  return parts.length > 0 ? parts.join('/') : str;
};

// Prevent form submit on press enter
export const filterEnterKey = (e) => {
  if (e.keyCode !== 13) return;
  if (e.target.type === 'textarea') return;
  if (e.target.getAttribute('allow-enter') === 'true') return;

  e.preventDefault();
};


// Event handler to intercept and control events
export const preventDefaults = (e) => {
  e.preventDefault();
  e.stopPropagation();
};